import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box, Chip, Stack, Tab, Tabs } from '@mui/material';
import React from 'react';

import type {
  ResolvedClassification,
  ResolvedFlag,
  ScreeningHistoryEntry,
  ScreeningTraceEntry,
} from '~/shared/utils/classification-scheme';
import { Drawer } from '~c/components/base/drawer';
import Text from '~c/components/base/text';

export interface StoredScreeningProfile {
  classification?: string | null;
  classifications?: ResolvedClassification[];
  flags?: ResolvedFlag[];
  inherentRisk?: string | null;
  rationaleTrace?: (Partial<ScreeningTraceEntry> & {
    ruleId: string;
    text?: string;
  })[];
  evaluatedAt?: string | null;
  answersChangedAt?: string | null;
  history?: ScreeningHistoryEntry[];
}

export const formatDate = (value: Date | string | null | undefined) =>
  value ? new Date(value).toLocaleString() : '—';

export const ClassChip: React.FC<{ label: string; color: string }> = ({
  label,
  color,
}) => (
  <Chip
    label={label}
    size="small"
    sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }}
  />
);

type DetailsTab = 'why' | 'history';

const snapshotLabel = (
  snapshot: ScreeningHistoryEntry['previous'] | undefined
): string => {
  if (!snapshot) {
    return 'None';
  }
  const classes = snapshot.classifications.length
    ? snapshot.classifications.map((c) => c.classLabel).join(' · ')
    : (snapshot.classification ?? 'None');
  const flags = snapshot.flags.map((f) => f.label);
  return flags.length ? `${classes} (flags: ${flags.join(', ')})` : classes;
};

const Muted: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Text sz="body14" color="text.secondary">
    {children}
  </Text>
);

interface Props {
  open: boolean;
  onClose: () => void;
  profile: StoredScreeningProfile | null;
}

const ScreeningDetailsDrawer: React.FC<Props> = ({
  open,
  onClose,
  profile,
}) => {
  const [tab, setTab] = React.useState<DetailsTab>('why');

  const trace = profile?.rationaleTrace ?? [];
  const matched = trace.filter((t) => t.passed !== false);
  const unmatched = trace.filter((t) => t.passed === false);
  const history = [...(profile?.history ?? [])].reverse();

  const renderRule = (t: (typeof trace)[number]) => (
    <Stack key={t.ruleId} direction="row" gap={1} alignItems="flex-start">
      {t.passed === false ? (
        <HighlightOffIcon fontSize="small" color="disabled" />
      ) : (
        <CheckCircleOutlineIcon fontSize="small" color="success" />
      )}
      <Box>
        <Text sz="bold-body14">
          {t.ruleName ?? t.ruleId}
          {t.classifications?.length
            ? ` → ${t.classifications.map((c) => c.classLabel).join(', ')}`
            : ''}
        </Text>
        {(t.reason ?? t.text ?? t.ruleDescription) && (
          <Muted>{t.reason ?? t.text ?? t.ruleDescription}</Muted>
        )}
      </Box>
    </Stack>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Screening classification"
      width={560}
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as DetailsTab)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab value="why" label="Why" />
        <Tab value="history" label={`History (${history.length})`} />
      </Tabs>

      {tab === 'why' && (
        <Stack spacing={2}>
          {(profile?.classifications ?? []).map((c) => (
            <Box key={c.key}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Text sz="bold-body14">{c.label}</Text>
                <ClassChip label={c.classLabel} color={c.color} />
              </Stack>
              {c.description && <Muted>{c.description}</Muted>}
            </Box>
          ))}
          <Text sz="bold-body16">
            Rules that matched ({matched.length} of {trace.length})
          </Text>
          {trace.length === 0 && (
            <Muted>No rule-by-rule detail was recorded.</Muted>
          )}
          {matched.map(renderRule)}
          {unmatched.length > 0 && (
            <>
              <Text sz="bold-body16">Rules that did not match</Text>
              {unmatched.map(renderRule)}
            </>
          )}
        </Stack>
      )}

      {tab === 'history' && (
        <Stack spacing={2}>
          {history.length === 0 && (
            <Muted>Not re-run since the system was created.</Muted>
          )}
          {history.map((h, i) => (
            <Box
              key={`${String(h.evaluatedAt)}-${i}`}
              sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Text sz="bold-body14">
                {formatDate(h.evaluatedAt)} · {h.evaluatedByName ?? 'Unknown'}
              </Text>
              <Text sz="body14">
                {snapshotLabel(h.previous)} → {snapshotLabel(h.current)}
              </Text>
              <Muted>Reason: {h.reason}</Muted>
            </Box>
          ))}
        </Stack>
      )}
    </Drawer>
  );
};

export default ScreeningDetailsDrawer;
